import { useEffect, useState } from "react";
import { attendanceStructureService } from "../services/api";
import { roundLeaveDaysForDisplay } from "./leaveBalanceDisplay";

/**
 * Year/month for leave-balance as-of the leave from-date (not "today").
 * Same rule as createLeaveRequest: later-month credits are not usable.
 */
export function yearMonthFromLeaveDate(iso) {
  const s = String(iso || "").trim().slice(0, 10);
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  if (Number.isFinite(y) && y >= 2000 && m >= 1 && m <= 12) {
    return { year: y, month: m };
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Closing balance on/before `month` — excludes later-month credits (Aug/Sep when applying in March).
 * Matches backend createLeaveRequest month-row selection.
 */
export function pickLeaveBalanceAsOfMonth(months, month) {
  const list = Array.isArray(months) ? months : [];
  const asOf = Number(month);
  const eligible = list.filter(
    (row) => !row?.ineligible && Number(row?.month) <= asOf
  );
  return (
    eligible.find((row) => Number(row.month) === asOf) ||
    eligible[eligible.length - 1] ||
    null
  );
}

/**
 * Fetch closing balance as of a leave month for one leave type (badge / apply form).
 * Pass year/month from the leave from-date so future credits are not shown.
 * @returns {{ loading: boolean, label: string|null, raw: number|null }}
 */
export function useLeaveTypeBalanceHint({
  employeeId,
  leaveTypeId,
  enabled = true,
  year,
  month,
  asOfDate,
}) {
  const asOf = yearMonthFromLeaveDate(asOfDate);
  const balanceYear = Number(year) >= 2000 ? Number(year) : asOf.year;
  const balanceMonth =
    Number(month) >= 1 && Number(month) <= 12 ? Number(month) : asOf.month;

  const [state, setState] = useState({
    loading: false,
    label: null,
    raw: null,
  });

  useEffect(() => {
    const empId = Number(employeeId);
    const typeId = Number(leaveTypeId);
    if (!enabled || !Number.isFinite(empId) || empId <= 0 || !Number.isFinite(typeId) || typeId <= 0) {
      setState({ loading: false, label: null, raw: null });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    attendanceStructureService
      .getLeaveBalance(empId, balanceYear, typeId)
      .then((data) => {
        if (cancelled) return;
        const months = Array.isArray(data)
          ? data
          : data?.months ?? data?.list ?? [];
        const row = pickLeaveBalanceAsOfMonth(months, balanceMonth);
        const closing = Number(row?.closingBalance ?? 0);
        const rounding = data?.policy?.balanceRounding ?? "full_day";
        const display = roundLeaveDaysForDisplay(closing, rounding);
        setState({
          loading: false,
          label: `Balance ${display}`,
          raw: closing,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, label: null, raw: null });
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, leaveTypeId, enabled, balanceYear, balanceMonth]);

  return state;
}

/** Resolve leave type DB id from a code against employee leave-type list. */
export function resolveLeaveTypeIdFromCode(leaveTypes, code) {
  const want = String(code || "").toUpperCase().trim();
  if (!want || !Array.isArray(leaveTypes)) return null;
  const hit = leaveTypes.find(
    (lt) =>
      String(lt.code || lt.shortCode || lt.leaveCode || "").toUpperCase().trim() ===
      want
  );
  const id = hit?.id ?? hit?.leaveTypeId ?? null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}
